import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ExternalLink, ArrowUpRight, CheckCircle2, ShieldCheck, Loader2 } from 'lucide-react';
import IslandBaseLead from '../components/IslandBaseLead';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PILLAR_SLUG_MAP = {
  relocation: 'relocation',
  'real-estate': 'real-estate',
  finance: 'finance',
  logistics: 'logistics',
};

export default function PillarPage({ pillarId: pillarIdProp }) {
  const params = useParams();
  const pillarId = pillarIdProp || PILLAR_SLUG_MAP[params.pillar] || 'relocation';
  const [pillar, setPillar] = useState(null);

  useEffect(() => {
    axios.get(`${API}/pillars/${pillarId}`).then((r) => setPillar(r.data)).catch(() => setPillar(null));
  }, [pillarId]);

  const onAffiliateClick = async (product) => {
    try {
      const { data } = await axios.post(`${API}/rescue/affiliate-click`, {
        product_id: product.id,
        referrer: typeof document !== 'undefined' ? document.referrer : null,
      });
      window.open(data.cta_url || product.cta_url, '_blank', 'noopener,noreferrer');
    } catch {
      window.open(product.cta_url, '_blank', 'noopener,noreferrer');
    }
  };

  if (!pillar) return <div className="px-6 py-32 text-center"><Loader2 className="text-[#D4AF37] animate-spin mx-auto" /></div>;

  return (
    <div className="px-6 md:px-12 lg:px-24 py-16">
      {/* Hero */}
      <section data-testid={`pillar-hero-${pillarId}`} className="mb-20">
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6 flex items-center gap-3">
          <ShieldCheck size={12} strokeWidth={1.5} />
          Sovereign Pillar · {pillar.title}
        </div>
        <h1 className="text-5xl md:text-6xl lg:text-7xl font-extralight leading-[1.02] tracking-tight max-w-5xl">
          <span className="font-display italic text-[#D4AF37]">{pillar.headline}</span>
        </h1>
        <p className="text-lg text-[#A3A3A3] mt-8 max-w-2xl leading-relaxed font-light">{pillar.blurb}</p>
        <div className="text-[10px] tracking-[0.4em] uppercase text-[#A3A3A3] mt-6">{pillar.tagline}</div>
      </section>

      {/* Phase-based Milestone Tracker (Relocation only) */}
      {pillar.milestones && (
        <section data-testid="milestone-tracker" className="mb-24">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">Milestone Tracker · Five Phases</div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {pillar.milestones.map((m, idx) => (
              <div key={m.stage} className="sov-border bg-[#0A0A0A] p-5 flex flex-col">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-full border border-[#D4AF37] flex items-center justify-center text-[10px] text-[#D4AF37] font-display">{idx + 1}</div>
                  <div className="text-[9px] tracking-[0.3em] uppercase text-[#A3A3A3]">{m.stage}</div>
                </div>
                <div className="font-display text-lg text-[#F5F5F5] mb-3 leading-tight">{m.label}</div>
                <ul className="space-y-2 text-[11px] text-[#A3A3A3] font-light leading-relaxed">
                  {m.items.map((it) => (
                    <li key={it} className="flex items-start gap-2">
                      <CheckCircle2 size={10} strokeWidth={1.5} className="text-[#D4AF37] flex-shrink-0 mt-1" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Highlights — for non-relocation pillars */}
      {pillar.highlights && (
        <section className="mb-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {pillar.highlights.map((h) => (
              <div key={h.label} className="sov-border bg-[#0A0A0A] p-6">
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2">{h.label}</div>
                <div className="font-display text-xl text-[#F5F5F5] leading-tight">{h.value}</div>
              </div>
            ))}
          </div>

          {/* Pillar-specific CTAs to existing app features */}
          {pillarId === 'real-estate' && (
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/vault" className="btn-sovereign">Enter The Vault <ArrowUpRight size={16} strokeWidth={1.5} /></Link>
              <Link to="/valuation" className="btn-sovereign">Valuation Shield <ArrowUpRight size={16} strokeWidth={1.5} /></Link>
              <Link to="/marketplace" className="btn-sovereign">Marketplace <ArrowUpRight size={16} strokeWidth={1.5} /></Link>
            </div>
          )}
          {pillarId === 'finance' && (
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/currency-bridge" className="btn-sovereign">Currency Bridge <ArrowUpRight size={16} strokeWidth={1.5} /></Link>
              <Link to="/fractional" className="btn-sovereign">Fractional Engine <ArrowUpRight size={16} strokeWidth={1.5} /></Link>
            </div>
          )}
        </section>
      )}

      {/* Vetted Partners (filtered to pillar) */}
      <section data-testid="pillar-affiliates" className="mb-24">
        <div className="flex items-baseline justify-between gap-4 flex-wrap mb-8">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-2">Vetted Partners · {pillar.affiliates.length} Tools</div>
            <h2 className="text-3xl md:text-4xl font-extralight tracking-tight">
              The <span className="font-display italic text-[#D4AF37]">{pillar.title}</span> stack.
            </h2>
          </div>
          <Link to="/affiliates" className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] hover:text-[#D4AF37] transition-colors flex items-center gap-2">
            All Fleet Affiliates <ArrowUpRight size={12} strokeWidth={1.5} />
          </Link>
        </div>

        {pillar.affiliates.length === 0 ? (
          <div className="text-sm text-[#A3A3A3] font-light text-center py-12 sov-border bg-[#0A0A0A]">
            Partners curating in progress.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pillar.affiliates.map((p) => (
              <div key={p.id} data-testid={`affiliate-card-${p.id}`} className="sov-border bg-[#0A0A0A] p-6 flex flex-col hover:bg-[#D4AF37]/5 transition-colors">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">{p.category}</div>
                  <span className={`text-[9px] tracking-[0.25em] uppercase px-2 py-0.5 border ${p.tier === 'Master Tier' ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-[#444] text-[#A3A3A3]'}`}>
                    {p.tier}
                  </span>
                </div>
                <div className="font-display text-xl text-[#F5F5F5] mb-1">{p.title}</div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-3">{p.vendor}</div>
                <p className="text-sm text-[#A3A3A3] font-light leading-relaxed mb-5 flex-1">{p.blurb}</p>
                {p.price_usd > 0 && (
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#666] mb-3">From ${p.price_usd}</div>
                )}
                <button
                  onClick={() => onAffiliateClick(p)}
                  data-testid={`affiliate-cta-${p.id}`}
                  className="mt-auto py-3 border border-[#D4AF37] text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase hover:bg-[#D4AF37] hover:text-[#050505] transition-colors flex items-center justify-center gap-2"
                >
                  <ExternalLink size={11} strokeWidth={1.5} /> Visit Partner
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Lead capture (only on Relocation pillar) */}
      {pillarId === 'relocation' && (
        <div className="border-t border-[#D4AF37]/15 pt-8">
          <IslandBaseLead compact />
        </div>
      )}
    </div>
  );
}
